import React, { useState } from 'react';
import { API_BASE } from '../apiBase';
export default function ProductImageGenerator() {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [productName, setProductName] = useState<string | null>(null);
  const [productLink, setProductLink] = useState<string | null>(null);
  const [html, setHtml] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setError(null);
    setImageDataUrl(null);
    setProductName(null);
    setProductLink(null);
    setHtml('');

    try {
      const resp = await fetch('/api/generate-product-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) {
        throw new Error(data.error || 'Failed to generate image');
      }
      setImageDataUrl(data.imageDataUrl);
      setProductName(data.product?.name || null);
      setProductLink(data.product?.link || null);
      setHtml(data.html || '');
    } catch (e: any) {
      setError(e?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const copyHtml = async () => {
    try {
      await navigator.clipboard.writeText(html);
      alert('HTML copied to clipboard');
    } catch { }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Product Image Generator</h2>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Describe the image you want to generate
        </label>
        <div className="flex gap-2">
          <input
            className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., A cozy living room with a handwoven rug"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
          <button
            onClick={handleGenerate}
            disabled={loading || !prompt.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors"
          >
            {loading ? 'Generating…' : 'Generate'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-600 rounded">{error}</div>
      )}

      {imageDataUrl && (
        <div className="mt-8 space-y-6">
          <div className="bg-white p-4 rounded-lg shadow-md">
            <img src={imageDataUrl} alt={`Generated: ${prompt}`} className="max-w-full h-auto rounded" />
            {productLink && productName && (
              <p className="mt-2 text-sm text-gray-600">
                Featured: <a href={productLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{productName}</a>
              </p>
            )}
            <div className="mt-4 flex gap-2">
              <button
                onClick={async () => {
                  if (!confirm('Schedule this image to your Posting Calendar?')) return;
                  try {
                    const res = await fetch(`${API_BASE}/api/calendar/posts/add`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        platform: 'instagram',
                        content: {
                          topic: prompt || 'New Image',
                          caption: `Check out this ${productName || 'image'}: ${prompt}`,
                          hashtags: ['style', 'design']
                        },
                        type: 'image_post'
                        // Optional: could pass imageDataUrl here if supported
                      })
                    });
                    const data = await res.json();
                    if (data.success) {
                      alert('✅ Image scheduled successfully!');
                    } else {
                      alert('❌ Failed to schedule: ' + data.error);
                    }
                  } catch (e) {
                    alert('❌ Error scheduling image');
                  }
                }}
                className="px-4 py-2 bg-green-600 text-white font-medium rounded hover:bg-green-700"
              >
                📅 Schedule
              </button>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">HTML Snippet</h3>
            <div className="bg-gray-100 p-4 rounded-md overflow-x-auto">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap">{html}</pre>
              <button
                onClick={copyHtml}
                className="mt-2 px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded"
              >
                Copy HTML
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
